/**
 * LoyaltyPill — compact "Play More, Pay Less" tier indicator for the Home page.
 * Shows current fee % and progress toward the loyalty tier (3.21% at 10+ completed games).
 * Only rendered when the user is logged in.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { Sparkles, TrendingDown } from 'lucide-react';

export const LoyaltyPill = () => {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/user/loyalty`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setData(r.data);
      } catch { /* silent — non-critical */ }
    })();
    return () => { cancelled = true; };
  }, [user, token]);

  if (!user || !data) return null;

  const { completed_games, current_tier, current_fee_pct, next_tier, next_tier_fee_pct, next_tier_min_games, games_to_next_tier, is_max_tier } = data;
  const progressPct = is_max_tier
    ? 100
    : Math.min(100, (completed_games / (next_tier_min_games || 1)) * 100);
  const tierColor = current_tier === 3 ? 'emerald' : current_tier === 2 ? 'amber' : 'sky';
  const cardClass = {
    emerald: 'border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-teal-500/5',
    amber:   'border-amber-500/40  bg-gradient-to-br from-amber-500/10  to-orange-500/5',
    sky:     'border-sky-500/30    bg-gradient-to-br from-sky-500/8    to-blue-500/5',
  }[tierColor];
  const iconBg = {
    emerald: 'bg-emerald-500/20',
    amber:   'bg-amber-500/20',
    sky:     'bg-sky-500/15',
  }[tierColor];
  const iconColor = {
    emerald: 'text-emerald-300',
    amber:   'text-amber-300',
    sky:     'text-sky-300',
  }[tierColor];

  return (
    <div data-testid="loyalty-pill" className={`rounded-xl border p-2.5 flex items-center gap-3 ${cardClass}`}>
      <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${iconBg}`}>
        {is_max_tier ? <Sparkles className={`w-4 h-4 ${iconColor}`} /> : <TrendingDown className={`w-4 h-4 ${iconColor}`} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <p className={`text-[11px] font-bold ${iconColor}`}>
            {is_max_tier ? 'Max loyalty tier — 3.21%' : `Tier ${current_tier} · Play More, Pay Less`}
          </p>
          <p className="text-[10px] font-mono text-white">
            {current_fee_pct.toFixed(2)}% fee
          </p>
        </div>
        <p className="text-[10px] text-slate-400 leading-snug">
          {is_max_tier ? (
            <>You've completed <span className="text-white font-semibold">{completed_games} games</span> — cheapest XRP room fees in the app.</>
          ) : (
            <>
              <span className="text-white font-semibold">{games_to_next_tier} more {games_to_next_tier === 1 ? 'game' : 'games'}</span> to drop your XRP-room fee to <span className="text-emerald-300 font-semibold">{next_tier_fee_pct.toFixed(2)}%</span> (Tier {next_tier})
            </>
          )}
        </p>
        {!is_max_tier && (
          <div className="mt-1 h-1 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-sky-500 via-amber-400 to-emerald-400 transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LoyaltyPill;
